import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="room-modal"
// Room manager + selector for a device check. Radios pick the room (an existing
// one, or the "new room" row); the choice is submitted with the assignment form
// on Save — a new room rides along as a client-generated UUID + room_name, which
// DeviceCheckSnapshot persists. Renaming and deleting are room-level operations
// handled inline against RoomsController (mirroring the mobile mutations), and
// the modal closes on the assignment form's turbo:submit-end.
export default class extends Controller {
  static targets = ["newRadio", "newInput", "newName"]
  static values = { confirmDelete: String }

  connect() {
    this.boundSubmitEnd = () => window.dispatchEvent(new Event("modal:close"))
    this.element.addEventListener("turbo:submit-end", this.boundSubmitEnd)
  }

  disconnect() {
    this.element.removeEventListener("turbo:submit-end", this.boundSubmitEnd)
  }

  selectExisting() {
    this.newNameTarget.value = ""
  }

  selectNew() {
    this.newRadioTarget.checked = true
    this.newNameTarget.value = this.newInputTarget.value.trim()
  }

  typeNew() {
    this.newRadioTarget.checked = true
    this.newNameTarget.value = this.newInputTarget.value.trim()
  }

  save() {
    if (this.newRadioTarget.checked) {
      const name = this.newInputTarget.value.trim()
      if (!name) {
        this.newInputTarget.focus()
        return
      }
      this.newNameTarget.value = name
    } else {
      this.newNameTarget.value = ""
    }
    this.newNameTarget.form.requestSubmit()
  }

  selectRow(event) {
    const input = event.currentTarget
    if (!input.readOnly) return

    input.closest("li").querySelector('input[type="radio"]').checked = true
    this.selectExisting()
  }

  startEdit(event) {
    this.beginEdit(event.currentTarget.closest("li"))
  }

  saveRename(event) {
    this.commitRow(event.currentTarget.closest("li"))
  }

  cancelEdit(event) {
    this.revertRow(event.currentTarget.closest("li"))
  }

  renameKey(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.commitRow(event.currentTarget.closest("li"))
    } else if (event.key === "Escape") {
      event.preventDefault()
      this.revertRow(event.currentTarget.closest("li"))
    }
  }

  beginEdit(li) {
    const input = li.querySelector('input[type="text"]')
    input.readOnly = false
    this.toggleRow(li, true)
    input.focus()
    input.select()
  }

  revertRow(li) {
    const input = li.querySelector('input[type="text"]')
    input.value = input.defaultValue
    input.readOnly = true
    this.toggleRow(li, false)
  }

  async commitRow(li) {
    const input = li.querySelector('input[type="text"]')
    const name = input.value.trim()

    if (!name) {
      input.focus()
      return
    }
    if (name !== input.defaultValue) {
      const response = await this.request(input.dataset.url, "PATCH", { room: { name } })
      if (!response.ok) {
        this.revertRow(li)
        return
      }
      input.defaultValue = name
      this.flashSaved(input)
    }
    input.readOnly = true
    this.toggleRow(li, false)
  }

  toggleRow(li, editing) {
    const input = li.querySelector('input[type="text"]')
    input.classList.toggle("border-gray-300", editing)
    input.classList.toggle("border-transparent", !editing)

    const visibility = { edit: !editing, trash: !editing, save: editing, cancel: editing }
    for (const [role, show] of Object.entries(visibility)) {
      li.querySelector(`[data-role="${role}"]`)?.classList.toggle("hidden", !show)
    }
  }

  flashSaved(input) {
    input.classList.add("bg-green-50", "border-green-400")
    setTimeout(() => input.classList.remove("bg-green-50", "border-green-400"), 1200)
  }

  async destroy(event) {
    if (!window.confirm(this.confirmDeleteValue)) return

    const button = event.currentTarget
    const response = await this.request(button.dataset.url, "DELETE")
    if (response.ok) button.closest("li").remove()
  }

  request(url, method, body = null) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    return fetch(url, {
      method,
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": token
      },
      body: body ? JSON.stringify(body) : null
    })
  }
}
